import React, { createContext, useContext, useState, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hrtrack_user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('hrtrack_token'));

  // Persist a completed session (token + user) into state + localStorage.
  const establishSession = useCallback((data) => {
    localStorage.setItem('hrtrack_token', data.token);
    localStorage.setItem('hrtrack_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (username, password) => {
    const data = await api.post('/auth/login', { username, password }).then(r => r.data);
    // Account has 2FA on: caller must complete the second step before we have a
    // real token. Surface the mfa handshake instead of a user.
    if (data.mfa_required) return { mfa_required: true, mfa_token: data.mfa_token };
    return establishSession(data);
  }, [establishSession]);

  // Complete a 2FA login with the short-lived mfa_token + a code.
  const verifyMfa = useCallback(async (mfa_token, code) => {
    const data = await api.post('/auth/login/verify-2fa', { mfa_token, code }).then(r => r.data);
    return establishSession(data);
  }, [establishSession]);

  const updateUser = useCallback((userData) => {
    localStorage.setItem('hrtrack_user', JSON.stringify(userData));
    setUser(userData);
    if (userData.token) {
      localStorage.setItem('hrtrack_token', userData.token);
      setToken(userData.token);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('hrtrack_token');
    localStorage.removeItem('hrtrack_user');
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, login, verifyMfa, logout, updateUser,
      isAdmin: user?.role === 'admin' || user?.role === 'manager',
      isManager: user?.role === 'manager',
      isSuperAdmin: user?.role === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
